import { HttpStatusCode } from '@angular/common/http';
import { Component, Injector, OnInit } from '@angular/core';
import { FirebaseError } from '@angular/fire/app';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ILoginRequest, getCurrentUser, login } from '@modeso/aumico-lib-users-fe';
import { Role } from '@modeso/aumico-lib-users-fe/src/lib/enums/role.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import Debug from 'debug';
import { EMPTY, catchError, filter, from, switchMap, take } from 'rxjs';
import { MfaService } from '../../../../../aumico-lib-users-fe/src/lib/services/mfa.service';
import { MixpanelService } from '../../../../../mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '../../../../../mixpanel/mixpanelEvents.enum';
import { Paths } from '../../enums/paths.enum';
import { UsersNavigationService } from '../../shared/services/users.navigation.service';
import getFirebaseErrorMessage from '../../shared/util/firebase.errors.messages';
import { BasePageComponent } from '../base.page';
import { localizationKeys } from './../../utils/localizationKeys';
const debug = Debug('aumico:aumico-project-reports:LoginPage');

@UntilDestroy()
@Component({
    selector: 'app-page-login',
    templateUrl: './login.page.html',
    styleUrls: ['./login.page.scss'],
})
export class LoginPage extends BasePageComponent implements OnInit {
    localizationKeys = localizationKeys;
    loginForm: UntypedFormGroup;
    isLoginFailed = false;
    errorMessage: string;

    // this handler is passed so its this is the action not the class,
    // making it an arrow function is better to maintain the this inside it's creation scope (the class's constructor)

    actionButton = {
        title: this.localizationKeys.aumico_reports_login_bottom_button_of_question_register,
        action: this.navigateToSignUpPage.bind(this),
    };
    actionButtonQuestion = this.localizationKeys.aumico_reports_login_bottom_question;
    constructor(
        injector: Injector,
        private usersNavigationService: UsersNavigationService,
        private readonly mixpanel: MixpanelService,
        private readonly mfaService: MfaService,
    ) {
        super(injector);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.usersNavigationService.readyToRoute$
            .pipe(
                filter((readyToRoute) => {
                    return readyToRoute !== null && readyToRoute !== undefined;
                }),
                untilDestroyed(this),
            )
            .subscribe((lang) => this.routeToFsOverViewPage(lang));

        this.loginForm = new UntypedFormGroup({
            userName: new UntypedFormControl(null, { validators: [Validators.required] }),
            password: new UntypedFormControl(null, { validators: [Validators.required] }),
        });

        this.actionsListener$
            .pipe(ofType(login.success), take(1), untilDestroyed(this))
            .subscribe(() => this.onLoginSuccessfully());

        this.actionsListener$
            .pipe(ofType(login.failure), untilDestroyed(this))
            .subscribe((response) => this.onLoginFailed(response.payload));

        this.actionsListener$
            .pipe(ofType(getCurrentUser.failure), untilDestroyed(this))
            .subscribe(({ payload: error }) => {
                if (error.status === HttpStatusCode.NotFound) {
                    this.isLoginFailed = true;
                    this.errorMessage = this.i18n.t('aumico_reports_login_credentials_error') as string;
                }
            });
    }

    private onLoginSuccessfully(): void {
        debug('onLoginSuccessfully');
        this.mixpanel.identify(this.loginForm.value.userName);
        this.mixpanel.track(MIXPANEL_EVENTS.LOGIN_SUCCESS);
        const lang = this.getCurrentLanguageWithFallBack();
        this.mfaService.checkMfa$()
            .pipe(
                switchMap((mfaCheck) => {
                    return this.mfaService.forceTokenRefresh$().pipe(
                        switchMap(() => from(this.auth.currentUser!.getIdTokenResult())),
                        switchMap((tokenResult) => {
                            const roles = tokenResult.claims['roles'] as string[] | undefined;
                            const validRoles = [Role.ORG_USER, Role.ORG_ADMIN, Role.ORG_VIEWER, '2FA_QUERY', '2FA_SETUP'];
                            const hasValidRole = roles?.some((r) => validRoles.includes(r as Role)) ?? false;
                            if (!hasValidRole) {
                                debug('Login blocked: user has no valid role. Roles found: %o', roles);
                                this.isLoginFailed = true;
                                this.errorMessage = this.i18n.t('aumico_reports_login_invalid_role_error') as string;
                                return this.auth.signOut().then(() => null);
                            }
                            return Promise.resolve(mfaCheck.status);
                        }),
                    );
                }),
                catchError((err) => {
                    debug('onLoginSuccessfully error', err);
                    this.authProvider.dispatchGetCurrentUser();
                    return EMPTY;
                }),
                untilDestroyed(this),
            )
            .subscribe({
                next: (status) => {
                    if (status === null) {
                        return; // blocked due to invalid role, already handled
                    }
                    if (status === 'SETUP_REQUIRED') {
                        this.router.navigate([`/${lang}/auth/2fa/setup`], {
                            queryParams: { returnUrl: this.safeReturnUrl() },
                        });
                    } else if (status === 'VERIFY_REQUIRED') {
                        this.router.navigate([`/${lang}/auth/2fa/verify`], {
                            queryParams: { returnUrl: this.safeReturnUrl() },
                        });
                    } else {
                        this.authProvider.dispatchGetCurrentUser();
                        this.routeToFsOverViewPage(lang);
                    }
                },
            });
    }

    private routeToFsOverViewPage(lang: string): void {
        const returnUrl = this.safeReturnUrl();
        if (returnUrl != null) {
            void this.router.navigateByUrl(returnUrl);
            return;
        }
        this.router.navigate([`/${lang}/${Paths.HOME}`]);
    }


    private onLoginFailed(error: FirebaseError): void {
        this.mixpanel.track(MIXPANEL_EVENTS.LOGIN_FAILED);
        this.errorMessage = getFirebaseErrorMessage(error);
        this.isLoginFailed = true;
    }

    onLoginUser(): void {
        const userData: ILoginRequest = {
            username: this.loginForm.value.userName,
            password: this.loginForm.value.password,
        };
        this.authProvider.login(userData);
    }

    navigateToSignUpPage(): void {
        const currentLanguage = this.getCurrentLanguageWithFallBack();
        this.router.navigate([`./${currentLanguage}`, Paths.AUTH, Paths.SIGNUP]);
    }
}
